import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, X, Trash2, Pencil, Receipt } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
import { fmtDate } from "../lib/dateFormat";

export default function Casetas() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]); const [assets, setAssets] = useState([]);
    const [filters, setFilters] = useState({ job_id: "", asset_id: "", from: "", to: "" });
    const [editing, setEditing] = useState(null); const [showForm, setShowForm] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filters.job_id) params.job_id = filters.job_id;
        if (filters.asset_id) params.asset_id = filters.asset_id;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;
        try {
            const r = await api.get("/tolls", { params });
            setRows(r.data);
        } finally { setLoading(false); }
    }, [filters]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/jobs").then(r => setJobs(r.data));
        api.get("/assets").then(r => setAssets(r.data));
    }, []);

    const totalAmount = rows.reduce((s, r) => s + r.amount_mxn, 0);
    const nameJob = (id) => (jobs.find(j => j.id === id) || {}).code || "—";
    const nameAsset = (id) => (assets.find(a => a.id === id) || {}).nickname || "—";

    return (
        <div className="space-y-6" data-testid="casetas-page">
            <header className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.tolls.title}</h1>
                    <p className="mt-2 text-slate-400 text-sm">{dict.tolls.subtitle}</p>
                </div>
                <button type="button" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-toll-btn"
                    className="h-10 px-4 rounded-lg bg-brand-orange hover:bg-brand-orange/90 text-white inline-flex items-center gap-2 text-sm font-semibold">
                    <Plus size={16}/>{dict.tolls.newToll}
                </button>
            </header>

            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5">
                <div className="text-[10px] uppercase tracking-wider text-amber-200/80">{dict.tolls.totalRegistered}</div>
                <div className="font-display text-3xl font-extrabold text-amber-200 mt-2" data-testid="total-tolls">{fmtMXN(totalAmount)}</div>
                <div className="text-xs text-amber-200/70 mt-1">{rows.length} {dict.tolls.tollsWord}</div>
            </div>

            <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                <select value={filters.job_id} onChange={e => setFilters({ ...filters, job_id: e.target.value })} data-testid="filter-job"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[200px]">
                    <option value="">{dict.tolls.allJobs}</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.code}</option>)}
                </select>
                <select value={filters.asset_id} onChange={e => setFilters({ ...filters, asset_id: e.target.value })} data-testid="filter-asset"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[200px]">
                    <option value="">{dict.tolls.allAssets}</option>
                    {assets.map(a => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                </select>
                <div className="flex items-center gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">{dict.tolls.from || "Desde"}</label>
                    <input type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from"
                        className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
                <div className="flex items-center gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">{dict.tolls.to || "Hasta"}</label>
                    <input type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to"
                        className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
                {(filters.from || filters.to || filters.job_id || filters.asset_id) && (
                    <button type="button" onClick={() => setFilters({ job_id: "", asset_id: "", from: "", to: "" })}
                        data-testid="filter-clear"
                        className="h-9 px-3 rounded-lg border border-slate-700 hover:border-brand-orange text-xs uppercase tracking-wider text-slate-300">
                        {dict.common.clear || "Limpiar"}
                    </button>
                )}
            </div>

            {loading ? <div className="text-center py-10"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <div className="rounded-2xl border border-slate-800 overflow-x-auto" data-testid="tolls-table">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                            <tr><th className="text-left p-3">{dict.tolls.cols.date}</th>
                                <th className="text-left p-3">{dict.tolls.cols.job}</th>
                                <th className="text-left p-3">{dict.tolls.cols.asset}</th>
                                <th className="text-left p-3">{dict.tolls.cols.location}</th>
                                <th className="text-right p-3">{dict.tolls.cols.amount}</th>
                                <th className="text-right p-3"></th></tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-t border-slate-800" data-testid={`toll-row-${r.id}`}>
                                    <td className="p-3 text-slate-400">{fmtDate(r.date)}</td>
                                    <td className="p-3 font-mono text-xs">{nameJob(r.job_id)}</td>
                                    <td className="p-3">{nameAsset(r.asset_id)}</td>
                                    <td className="p-3">{r.location || "—"}</td>
                                    <td className="p-3 text-right font-mono">{fmtMXN(r.amount_mxn)}</td>
                                    <td className="p-3 text-right space-x-1">
                                        <button type="button" onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`edit-toll-${r.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-700 hover:bg-slate-800"><Pencil size={12}/></button>
                                        {isAdmin && <button type="button" onClick={() => setConfirmDel(r)} data-testid={`del-toll-${r.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 size={12}/></button>}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="px-0 py-0"><EmptyState icon={Receipt} title={dict.tolls.empty} ctaLabel={dict.tolls.newToll || "Registrar caseta"} onCta={() => { setEditing(null); setShowForm(true); }} testid="tolls-empty"/></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && <TollForm initial={editing} jobs={jobs} assets={assets}
                onClose={() => setShowForm(false)}
                onSaved={() => { setShowForm(false); load(); }}/>}
            {confirmDel && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900 w-full max-w-md p-6">
                        <h2 className="font-display text-2xl font-bold">{dict.tolls.confirmDel}</h2>
                        <p className="text-slate-400 text-sm mt-2">{nameJob(confirmDel.job_id)} • {confirmDel.location}</p>
                        <div className="flex gap-2 justify-end mt-5">
                            <button type="button" onClick={() => setConfirmDel(null)} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">{dict.common.cancel}</button>
                            <button type="button" data-testid="confirm-del-toll" onClick={async () => { await api.delete(`/tolls/${confirmDel.id}`); setConfirmDel(null); load(); }} className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold">{dict.common.confirm}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TollForm({ initial, jobs, assets, onClose, onSaved }) {
    const [form, setForm] = useState({
        job_id: initial?.job_id || (jobs[0]?.id || ""),
        asset_id: initial?.asset_id || "",
        date: initial?.date || new Date().toISOString().slice(0, 10),
        location: initial?.location || "",
        amount_mxn: initial?.amount_mxn || "",
        notes: initial?.notes || "",
    });
    const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const body = { ...form, amount_mxn: Number(form.amount_mxn), asset_id: form.asset_id || null };
            if (initial?.id) await api.patch(`/tolls/${initial.id}`, body);
            else await api.post("/tolls", body);
            onSaved();
        } catch (e2) { setErr(formatApiError(e2)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="toll-form-modal">
            <form noValidate onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900 w-full max-w-lg p-6 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-bold">{initial ? dict.tolls.editToll : dict.tolls.newToll}</h2>
                    <button type="button" onClick={onClose} data-testid="toll-form-close"><X size={20} className="text-slate-500 hover:text-slate-100"/></button>
                </div>
                <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.cols.job}</div>
                    <select required value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })} data-testid="toll-form-job" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700">
                        {jobs.map(j => <option key={j.id} value={j.id}>{j.code}</option>)}
                    </select>
                </label>
                <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.cols.asset} ({dict.common.optional})</div>
                    <select value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} data-testid="toll-form-asset" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700">
                        <option value="">—</option>
                        {assets.map(a => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                    </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.cols.date}</div>
                        <input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="toll-form-date" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700"/>
                    </label>
                    <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.cols.amount}</div>
                        <input type="number" required step="0.01" min="0.01" value={form.amount_mxn} onChange={e => setForm({ ...form, amount_mxn: e.target.value })} data-testid="toll-form-amount" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 font-mono"/>
                    </label>
                </div>
                <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.cols.location}</div>
                    <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="toll-form-location" placeholder={dict.tolls.locationPh} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700"/>
                </label>
                <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.tolls.notesLabel}</div>
                    <textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="toll-form-notes" className="w-full h-20 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </label>
                {err && <div className="text-sm text-red-300" data-testid="toll-form-error">{err}</div>}
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">{dict.common.cancel}</button>
                    <button type="submit" disabled={saving} data-testid="toll-form-submit" className="h-10 px-4 rounded-lg bg-brand-orange text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                        {saving && <Loader2 size={14} className="animate-spin"/>}{initial ? dict.common.save : dict.common.create}
                    </button>
                </div>
            </form>
        </div>
    );
}
